var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../services/api-client';
const DAY_MAP = {
    'monday': 'Mon', 'tuesday': 'Tue', 'wednesday': 'Wed',
    'thursday': 'Thu', 'friday': 'Fri', 'saturday': 'Sat', 'sunday': 'Sun',
};
let JvAdminSettings = class JvAdminSettings extends LitElement {
    constructor() {
        super(...arguments);
        this._settings = null;
        this._loading = true;
        this._saving = false;
        this._saved = false;
        this._openHour = '08';
        this._openMin = '00';
        this._closeHour = '17';
        this._closeMin = '30';
        this._days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
        this._quota = 300;
    }
    static { this.styles = css `
    :host { display: block; }
    .section { margin-bottom: 28px; }
    .day-btn { padding: 8px 12px; border-radius: 10px; font-size: 13px; font-weight: 600; border: 1px solid var(--jv-border); background: var(--jv-surface); cursor: pointer; transition: all .12s; }
    .day-btn.active { background: var(--jv-primary); color: #fff; border-color: var(--jv-primary); }
  `; }
    connectedCallback() {
        super.connectedCallback();
        void this._load();
    }
    async _load() {
        this._loading = true;
        try {
            this._settings = await api.get('/api/admin/tenant/settings');
            if (this._settings) {
                const parts = this._settings.openingTime.split(':');
                this._openHour = parts[0] ?? '08';
                this._openMin = parts[1] ?? '00';
                const cparts = this._settings.closingTime.split(':');
                this._closeHour = cparts[0] ?? '17';
                this._closeMin = cparts[1] ?? '30';
                this._quota = this._settings.defaultQuotaMinutesPerMonth;
                const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                this._days = allDays.filter((_, i) => (this._settings.operatingDays & (1 << i)) !== 0);
            }
        }
        catch { /* handled */ }
        finally {
            this._loading = false;
        }
    }
    _toggleDay(day) {
        this._saved = false;
        this._days = this._days.includes(day)
            ? this._days.filter(d => d !== day)
            : [...this._days, day];
    }
    _daysValue() {
        const all = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        return this._days.reduce((val, d) => val | (1 << all.indexOf(d)), 0);
    }
    async _save() {
        this._saving = true;
        this._saved = false;
        try {
            await api.patch('/api/admin/tenant/settings', {
                openingTime: `${this._openHour}:${this._openMin}`,
                closingTime: `${this._closeHour}:${this._closeMin}`,
                operatingDays: this._daysValue(),
                defaultQuotaMinutesPerMonth: this._quota,
            });
            this._saved = true;
        }
        catch { /* handled */ }
        finally {
            this._saving = false;
        }
    }
    _back() {
        this.dispatchEvent(new CustomEvent('admin-back', { bubbles: true, composed: true }));
    }
    render() {
        return html `
      <div class="max-w-content mx-auto px-5 sm:px-8 py-7">
        <div class="flex items-center gap-4 mb-6">
          <button @click=${this._back} class="text-sm text-jv-primary font-semibold hover:underline">&larr; Dashboard</button>
          <h1 class="text-2xl font-extrabold text-jv-ink">Settings</h1>
        </div>

        ${this._loading ? html `<p class="text-jv-ink-2 text-sm">Loading…</p>` : html `
          <div class="max-w-lg space-y-6">
            <section class="jv-card"><div class="p-5">
              <h2 class="font-bold text-jv-ink mb-4">Operating Hours</h2>
              <div class="grid grid-cols-2 gap-4 mb-4">
                <div><label class="jv-label">Opens at</label>
                  <div class="flex gap-2">
                    <input class="jv-input w-16" .value=${this._openHour} @input=${(e) => { this._openHour = e.target.value; this._saved = false; }} maxlength="2" />
                    <span class="self-center text-jv-ink-2">:</span>
                    <input class="jv-input w-16" .value=${this._openMin} @input=${(e) => { this._openMin = e.target.value; this._saved = false; }} maxlength="2" />
                  </div>
                </div>
                <div><label class="jv-label">Closes at</label>
                  <div class="flex gap-2">
                    <input class="jv-input w-16" .value=${this._closeHour} @input=${(e) => { this._closeHour = e.target.value; this._saved = false; }} maxlength="2" />
                    <span class="self-center text-jv-ink-2">:</span>
                    <input class="jv-input w-16" .value=${this._closeMin} @input=${(e) => { this._closeMin = e.target.value; this._saved = false; }} maxlength="2" />
                  </div>
                </div>
              </div>
              <label class="jv-label mb-2">Active days</label>
              <div class="flex flex-wrap gap-2 mb-4">
                ${['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(d => html `
                  <button class="day-btn ${this._days.includes(d) ? 'active' : ''}" @click=${() => this._toggleDay(d)}>${DAY_MAP[d]}</button>
                `)}
              </div>
            </div></section>

            <section class="jv-card"><div class="p-5">
              <h2 class="font-bold text-jv-ink mb-4">Quota</h2>
              <label class="jv-label">Minutes per parent per month</label>
              <input class="jv-input" type="number" .value=${String(this._quota)} @input=${(e) => { this._quota = parseInt(e.target.value) || 0; this._saved = false; }} />
              <p class="text-xs text-jv-ink-2 mt-2">${Math.floor(this._quota / 60)}h ${this._quota % 60}m per parent</p>
            </div></section>

            <div class="flex items-center gap-3">
              <button @click=${this._save} ?disabled=${this._saving} class="jv-btn-primary">
                ${this._saving ? 'Saving…' : 'Save Settings'}
              </button>
              ${this._saved ? html `<span class="text-jv-success text-sm font-semibold">Saved</span>` : ''}
            </div>
          </div>
        `}
      </div>
    `;
    }
};
__decorate([
    state()
], JvAdminSettings.prototype, "_settings", void 0);
__decorate([
    state()
], JvAdminSettings.prototype, "_loading", void 0);
__decorate([
    state()
], JvAdminSettings.prototype, "_saving", void 0);
__decorate([
    state()
], JvAdminSettings.prototype, "_saved", void 0);
__decorate([
    state()
], JvAdminSettings.prototype, "_openHour", void 0);
__decorate([
    state()
], JvAdminSettings.prototype, "_openMin", void 0);
__decorate([
    state()
], JvAdminSettings.prototype, "_closeHour", void 0);
__decorate([
    state()
], JvAdminSettings.prototype, "_closeMin", void 0);
__decorate([
    state()
], JvAdminSettings.prototype, "_days", void 0);
__decorate([
    state()
], JvAdminSettings.prototype, "_quota", void 0);
JvAdminSettings = __decorate([
    customElement('jv-admin-settings')
], JvAdminSettings);
export { JvAdminSettings };
